import React, { useEffect, useState } from 'react';
import AdminLayout from '../../../components/layout/AdminLayout';
import Card from '../../../components/common/Card';
import PinTable from '../../../components/tables/PinTable';
import Button from '../../../components/common/Button';
import FormField from '../../../components/common/FormField';
import { usePinSearch, useDeletePin } from '../../../hooks/usePinApi';
import AddNewButton from '../../../components/common/AddButton';
import { confirmDelete } from '../../../utils/sweetAlertUtils';
import { PinDTO } from '../../../services/apiClient';
import { useNavigate } from 'react-router-dom';

const PinList: React.FC = () => {
  const [formFilters, setFormFilters] = useState<any>({}); // Form input values
  const [searchFilters, setSearchFilters] = useState<any>({}); // Actual search criteria
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [items, setItems] = useState<PinDTO[]>([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [isLoading, setIsLoading] = useState(false);

  const { search } = usePinSearch();
  const deletePin = useDeletePin();

  const navigate = useNavigate();
  const breadcrumbs = [
    { label: 'Admin', href: '#' },
    { label: 'Pins', href: '#' },
  ];

  const totalPages = Math.ceil(totalRecords / pageSize);

  const fetchItems = async () => {
    setIsLoading(true);
    try {
      const response = await search({
        page: currentPage,
        pageSize,
        sortBy: 'Name',
        sortDescending: false,
        filters: searchFilters,
      });
      setItems(response.results);
      setTotalRecords(response.totalCount);
    } catch (error) {
      console.error('Error fetching Pins:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchItems();
  }, [currentPage, pageSize, searchFilters]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setCurrentPage(1);
    setSearchFilters(formFilters);
  };

  const handleClearSearch = () => {
    setFormFilters({});
    setSearchFilters({});
    setCurrentPage(1);
  };

  const handleDelete = async (dto: PinDTO) => {
    const confirmed = await confirmDelete();
    if (!confirmed) return;

    try {
      await deletePin.mutateAsync(dto.id || '');
      await fetchItems();
    } catch (error) {
      console.error('Error deleting Pin:', error);
    }
  };

  return (
    <AdminLayout
      activeMenuItem="/admin/pins"
      breadcrumbs={breadcrumbs}
      allowedRoles={['Admin']}
      requiredPermission="Settings.Pin.View"
    >
      <Card
        title="Manage Pins"
        subtitle={`Total Records: ${totalRecords}`}
        headerActions={
          <AddNewButton onClick={() => navigate('/admin/pins/create')} />
        }
      >
        <form onSubmit={handleSearch} className="mb-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
            <FormField
              id="name"
              label="Search by Name"
              value={formFilters.name || ''}
              onChange={(e) =>
                setFormFilters({ ...formFilters, name: e.target.value })
              }
              placeholder="Search by Name"
            />
          </div>

          <div className="flex space-x-4 mb-4">
            <Button variant="secondary" onClick={handleClearSearch}>
              Clear Search
            </Button>
            <Button type="submit">Search</Button>
          </div>
        </form>

        <PinTable
          data={items}
          handleEdit={(dto) => navigate(`/admin/pins/${dto.id}`)}
          handleDelete={handleDelete}
          currentPage={currentPage}
          totalPages={totalPages}
          setCurrentPage={setCurrentPage}
          pageSize={pageSize}
          setPageSize={setPageSize}
          isLoading={isLoading}
        />
      </Card>
    </AdminLayout>
  );
};

export default PinList;
